/**
 * User Service
 *
 * Manages the current authenticated user's state throughout the application.
 * Uses RxJS BehaviorSubject to provide reactive access to user information.
 *
 * @example
 * ```typescript
 * // In a component
 * constructor(private userService: UserService) {}
 *
 * ngOnInit() {
 *   // Subscribe to user changes
 *   this.userService.currentUser$.subscribe(user => {
 *     if (user) {
 *       console.log('Current user:', user.name);
 *     }
 *   });
 *
 *   // Check if user is super admin
 *   if (this.userService.isSuperAdmin()) {
 *     // Show admin-only features
 *   }
 * }
 * ```
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from '../models';

/**
 * Key used to store user data in localStorage
 */
const USER_STORAGE_KEY = 'blueride_current_user';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  /**
   * Internal BehaviorSubject holding the current user state
   * Private to prevent direct mutation from outside this service
   * Initialized with stored user from localStorage
   */
  private currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(
    this.getStoredUser()
  );

  /**
   * Retrieves stored user from localStorage
   */
  private getStoredUser(): User | null {
    try {
      const userJson = localStorage.getItem(USER_STORAGE_KEY);
      if (userJson) {
        return JSON.parse(userJson) as User;
      }
    } catch (error) {
      console.error('Error parsing stored user:', error);
      localStorage.removeItem(USER_STORAGE_KEY);
    }
    return null;
  }

  /**
   * Observable stream of the current user
   *
   * Components should subscribe to this to react to user state changes.
   * Emits null when no user is authenticated.
   *
   * @example
   * ```typescript
   * this.userService.currentUser$.subscribe(user => {
   *   if (user) {
   *     this.userName = user.name;
   *   }
   * });
   * ```
   */
  readonly currentUser$: Observable<User | null> = this.currentUserSubject.asObservable();

  /**
   * Gets the current user synchronously
   *
   * @returns The current user or null if not authenticated
   *
   * @example
   * ```typescript
   * const user = this.userService.currentUser;
   * if (user) {
   *   console.log('Logged in as:', user.email);
   * }
   * ```
   */
  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Sets the current user
   *
   * Typically called after successful login or token refresh.
   * Emits the new user to all subscribers and persists to localStorage.
   *
   * @param user - User object to set as current
   *
   * @example
   * ```typescript
   * this.userService.setCurrentUser(response.user);
   * ```
   */
  setCurrentUser(user: User): void {
    try {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Error storing user:', error);
    }
    this.currentUserSubject.next(user);
  }

  /**
   * Clears the current user
   *
   * Typically called during logout.
   * Emits null to all subscribers and removes from localStorage.
   *
   * @example
   * ```typescript
   * this.userService.clearCurrentUser();
   * ```
   */
  clearCurrentUser(): void {
    localStorage.removeItem(USER_STORAGE_KEY);
    this.currentUserSubject.next(null);
  }

  /**
   * Checks if the current user is a Company Super Admin
   *
   * Super admins have full access to all features including admin management.
   *
   * @returns True if the current user has the 'comp_superadmin' role
   *
   * @example
   * ```typescript
   * if (this.userService.isSuperAdmin()) {
   *   // Show admin management menu
   * }
   * ```
   */
  isSuperAdmin(): boolean {
    const user = this.currentUser;
    return user?.role?.name === 'comp_superadmin';
  }

  /**
   * Checks if the current user is a Company Admin
   *
   * Company admins have limited access based on their assigned schools.
   *
   * @returns True if the current user has the 'comp_admin' role
   *
   * @example
   * ```typescript
   * if (this.userService.isAdmin()) {
   *   // Show school-specific features
   * }
   * ```
   */
  isAdmin(): boolean {
    const user = this.currentUser;
    return user?.role?.name === 'comp_admin';
  }

  /**
   * Gets the list of school IDs the current user has access to
   *
   * Returns an empty array for super admins (they have access to all schools)
   * or for unauthenticated users.
   *
   * @returns Array of school IDs the user can access
   *
   * @example
   * ```typescript
   * const schoolIds = this.userService.getAccessibleSchoolIds();
   * this.loadDataForSchools(schoolIds);
   * ```
   */
  getAccessibleSchoolIds(): number[] {
    const user = this.currentUser;
    if (!user) {
      return [];
    }
    // Super admins have access to all schools (empty array indicates all access)
    if (this.isSuperAdmin()) {
      return [];
    }
    return user.school_ids || [];
  }
}
